'use client';

import { useEffect, useState } from 'react';

import useStores from 'app-migration/store';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useHistory } from 'react-router';
import { IncomingMessageType } from 'zh-web-sdk';

import { DEBIT_DEPOSIT_AMOUNT, DEBIT_DEPOSIT_FEE, DEBIT_WITHDRAWAL_AMOUNT } from 'constants/session';
import { FundMethods } from 'stores/ui/registration/registration';
import { storeImpactClickId } from 'utils/analytics/providers/impact';
import { fireDepositedPostBacks } from 'utils/analytics/providers/predictionNews';
import config from 'utils/config';
import { getLocalStorageWithExpiration } from 'utils/storage';

import useCryptoExternalSources from './useCryptoExternalSources';
import useFinancialsExternalSources from './useFinancialsExternalSources';
import useWorldApp from './useWorldApp';

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
// declare const tatari: any;

const useRouteLogic = (isAppRouter?: boolean) => {
    const rootStore = useStores();
    const uiStore = rootStore.uiStore;
    const depositDialog = uiStore.depositDialog;
    const withdrawalDialog = uiStore.withdrawalDialog;
    const registrationStore = uiStore.registration;
    const traderStore = rootStore.traderStore;
    const paymentsStore = traderStore.payments;
    const bankStore = traderStore.bank;
    const userStore = traderStore.user;
    const userPoperties = userStore.properties;
    const isLoggedIn = userStore.isLoggedIn;
    const exchangeStore = rootStore.exchangeStore;
    const cryptoStore = traderStore.crypto;

    const [ifHasLoadedGlobalVendors, setIfHasLoadedGlobalVendors] = useState(false);

    // Controls data fetch for the financials and crypto underlyings
    useFinancialsExternalSources();
    useCryptoExternalSources();

    useWorldApp();

    const nextUseRouter = useRouter();
    const nextPathname = usePathname();
    const nextSearchParams = useSearchParams();

    const history = useHistory();
    const reactPathname = history?.location.pathname;
    const reactSearchParams = location.search;

    const router = isAppRouter ? nextUseRouter : history;
    const pathname = isAppRouter ? nextPathname : reactPathname;
    const searchParams = isAppRouter ? nextSearchParams : reactSearchParams;
    const urlSearchParams = new URLSearchParams(searchParams.toString());
    const params = Object.fromEntries(urlSearchParams.entries());

    const replaceRoute = (searchParams: URLSearchParams) =>
        isAppRouter
            ? router.replace(`?${searchParams.toString()}`)
            : history.replace({
                  search: searchParams.toString(),
              });

    useEffect(
        () => {
            // Set the UTM data
            uiStore.setUTMData({
                utm_campaign: uiStore.utmData.utm_campaign || params.utm_campaign,
                utm_medium: uiStore.utmData.utm_medium || params.utm_medium,
                utm_content: uiStore.utmData.utm_content || params.utm_content,
                utm_source: uiStore.utmData.utm_source || params.utm_source,
                utm_term: uiStore.utmData.utm_term || params.utm_term,
                utm_landing: uiStore.utmData.utm_landing || pathname,
            });
            uiStore.setSource(uiStore.source || params.source);
            uiStore.setReferral(uiStore.referral || params.referral);

            // Capture and store Impact.com click ID
            if (params['irclickid']) {
                storeImpactClickId(params['irclickid']);
            }

            // Checkout callback params
            const storedPaymentId = getLocalStorageWithExpiration('DEBIT_DEPOSIT_PAYMENT_ID');
            if (params['cko-payment-id'] || params['cko-session-id'] || storedPaymentId) {
                // Try to get payment ID from URL params or localStorage
                const paymentId = params['cko-payment-id'] || storedPaymentId;

                // Retrieve the deposit amount from the local storage
                const depositAmount = parseFloat(getLocalStorageWithExpiration(DEBIT_DEPOSIT_AMOUNT) || '0');
                const depositFee = parseFloat(getLocalStorageWithExpiration(DEBIT_DEPOSIT_FEE) || '0');
                const withdrawalAmount = parseFloat(getLocalStorageWithExpiration(DEBIT_WITHDRAWAL_AMOUNT) || '0');
                const isWithdrawal = withdrawalAmount > 0;
                const amount = isWithdrawal ? withdrawalAmount : depositAmount;

                // Clear the local storage
                localStorage.removeItem(DEBIT_DEPOSIT_AMOUNT);
                localStorage.removeItem(DEBIT_DEPOSIT_FEE);
                localStorage.removeItem(DEBIT_WITHDRAWAL_AMOUNT);
                localStorage.removeItem('DEBIT_DEPOSIT_PAYMENT_ID');

                const isSignUp = pathname.includes('sign-up');

                if (!isSignUp && (paymentsStore.paymentStatus === 'failure' || amount > 0)) {
                    if (isWithdrawal) {
                        withdrawalDialog.open();
                    }
                }
                registrationStore.setFundMethod(FundMethods.DEBIT_CARD);
                if (paymentsStore.paymentStatus === 'failure') {
                    if (isWithdrawal) {
                        withdrawalDialog.setFailed();
                    } else if (paymentId) {
                        paymentsStore.getDebitPaymentStatus(
                            userStore.getUserId,
                            paymentId,
                            (title, reason) => {
                                if (!isSignUp) {
                                    depositDialog.open();
                                }
                                depositDialog.setStep('failed');
                                depositDialog.setErrorCode('debit_card_payment_failed_3ds');
                                depositDialog.setErrorTitle(title || 'Your deposit failed');
                                depositDialog.setErrorMessage(reason || 'Please try again');
                            },
                            () => {
                                if (!isSignUp) {
                                    depositDialog.open();
                                }
                                depositDialog.setStep('failed');
                                depositDialog.setErrorCode('debit_card_payment_failed_3ds');
                                depositDialog.setErrorTitle('Your deposit failed');
                                depositDialog.setErrorMessage('Please contact your card provider');
                            },
                        );
                    } else {
                        // No payment ID available, show generic error
                        if (!isSignUp) {
                            depositDialog.open();
                        }
                        depositDialog.setStep('failed');
                        depositDialog.setErrorCode('debit_card_payment_failed_3ds');
                        depositDialog.setErrorTitle('Your deposit failed');
                        depositDialog.setErrorMessage('Please contact your card provider');
                    }
                } else if (amount > 0) {
                    if (!isWithdrawal) {
                        // Set up dialog state before opening
                        depositDialog.setAmount(depositAmount);
                        depositDialog.setFee(depositFee);
                        depositDialog.loading = true;
                        depositDialog.setStep('review'); // Ensure we're on review step for loading display

                        if (!isSignUp) {
                            depositDialog.open();
                        }

                        // Poll payment status if we have a payment ID
                        if (paymentId) {
                            const intervalId = setInterval(() => {
                                paymentsStore.getDebitPaymentStatus(
                                    userStore.getUserId,
                                    paymentId,
                                    (title, reason) => {
                                        if (title || reason) {
                                            // Payment failed
                                            clearInterval(intervalId);
                                            depositDialog.setStep('failed');
                                            depositDialog.setErrorCode('debit_card_payment_failed_3ds');
                                            depositDialog.setErrorTitle(title || 'Payment failed');
                                            depositDialog.setErrorMessage(reason || 'Please try again');
                                            depositDialog.loading = false;
                                        } else {
                                            // Payment succeeded
                                            clearInterval(intervalId);
                                            paymentsStore.setPaymentStatus('success');
                                            depositDialog.setStep('initiated');
                                            depositDialog.setAmount(amount);
                                            depositDialog.setFee(depositFee);
                                            depositDialog.loading = false;
                                        }
                                    },
                                    () => {
                                        // Keep polling on error
                                    },
                                );
                            }, 2000);

                            // Timeout after 60 seconds
                            setTimeout(() => {
                                clearInterval(intervalId);
                                depositDialog.setStep('initiated');
                                depositDialog.setAmount(amount);
                                depositDialog.loading = false;
                            }, 60000);
                        } else {
                            // No payment ID, show success immediately
                            depositDialog.setStep('initiated');
                            depositDialog.setAmount(amount);
                            depositDialog.setFee(depositFee);
                            depositDialog.loading = false;
                        }

                        fireDepositedPostBacks(bankStore.depositsCount, amount, uiStore.getPredictionNewsUserId());
                        // tatari.track('Deposited', { amount: amount, userId: traderStore.user.getUserId });
                    } else {
                        withdrawalDialog.setReviewing();
                        withdrawalDialog.setAmount(amount);
                        withdrawalDialog.setPaymentId(paymentId);
                        withdrawalDialog.setFundMethod(FundMethods.DEBIT_CARD);
                    }
                }

                // Clean up the URL params
                urlSearchParams.delete('cko-payment-id');
                urlSearchParams.delete('cko-session-id');
                urlSearchParams.delete('cko-payment-session-id');
                replaceRoute(urlSearchParams);
            }

            // Prediction News user id
            const preditionNewsUserId = params['prediction_news_user_id'];
            if (preditionNewsUserId) {
                if (!isLoggedIn) {
                    uiStore.setPredictionNewsUserId(preditionNewsUserId);
                } else {
                    // Set the user id in the user properties
                    userPoperties.setPredictionNewsUserId(preditionNewsUserId);
                }

                // Remove user_id from URL
                urlSearchParams.delete('prediction_news_user_id');
                replaceRoute(urlSearchParams);
            }
        }, // TODO: check or fix hooks error
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [params],
    );

    const updateSources = (): void => {
        const source = uiStore.source;
        if (source) {
            const sources = userPoperties.sources;
            sources.push({ source: source, timestamp: new Date().toISOString() });
            userPoperties.setSources(sources);
            uiStore.setSource(undefined);
            urlSearchParams.delete('source');
            replaceRoute(urlSearchParams);
        }
    };

    useEffect(
        () => {
            if (userStore.isLoggedIn && !!uiStore.source) {
                userStore.loadProperties(updateSources);
            }
        }, // TODO: check or fix hooks error
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [uiStore.source, userStore.isLoggedIn],
    );

    useEffect(
        () => {
            if (!ifHasLoadedGlobalVendors) {
                for (const word of config.logRocketSanitizeWords) {
                    if (urlSearchParams.has(word)) {
                        return;
                    }
                }

                rootStore.loadGlobalVendors();
                setIfHasLoadedGlobalVendors(true);
            }
        }, // TODO: check or fix hooks error
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [urlSearchParams, ifHasLoadedGlobalVendors],
    );

    useEffect(() => {
        exchangeStore.load({ cached: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rootStore]);

    const startCryptoWithdrawal = (walletId: string): void => {
        withdrawalDialog.setFundMethod(FundMethods.CRYPTO);
        withdrawalDialog.setPrereviewing();
        withdrawalDialog.setCryptoWalletId(walletId);
        withdrawalDialog.open();
    };

    // Listen for messages from the ZeroHash iframe
    useEffect(() => {
        window.addEventListener('message', (message) => {
            if (!message.data) return;

            // ZH deposit modal close callback
            if (message.data.type === IncomingMessageType.FundCloseButtonClicked) {
                userStore.metrics.recordDepositMetric({
                    step: 'closeCryptoDeposit',
                });
                uiStore.setShowZeroHashCloseDialog(true);
            }

            // ZH account link callback
            if (message.data.type === 'CRYPTO_ACCOUNT_LINK_EXTERNAL_ACCOUNT_CREATED') {
                // clone the old wallets
                const oldWallets = cryptoStore.cryptoWallets.slice();
                setTimeout(() => {
                    cryptoStore.loadCryptoWallets(userStore.user.user_id, (wallets) => {
                        const newWallet = wallets.find(
                            (wallet) => !oldWallets.some((oldWallet) => oldWallet.id === wallet.id),
                        );
                        if (newWallet) {
                            startCryptoWithdrawal(newWallet.id);
                        }
                    });
                }, 2000);
            }

            // ZH withdrawal callback success
            if (message.data.type === 'CRYPTO_WITHDRAWALS_COMPLETED') {
                withdrawalDialog.setInitiated();
            }

            // ZH withdrawal callback failed
            if (message.data.type === 'CRYPTO_WITHDRAWALS_FAILED') {
                withdrawalDialog.setFailed();
            }

            // ZH withdrawal callback closed modal
            if (message.data.type === 'CRYPTO_WITHDRAWALS_CLOSE_BUTTON_CLICKED') {
                if (!withdrawalDialog.isInitiated) {
                    withdrawalDialog.setFailed();
                }
            }
        });

        return () => {
            window.removeEventListener('message', () => {});
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uiStore]);
};

export default useRouteLogic;
